import ErrorResponse from '@/models/error/ErrorResponse'
import ErrorResponseType from '@/shared/constants/error/ErrorResponseType'
import { CreateBookingErrorCodes } from '@/shared/constants/booking/CreateBookingErrorCodes'

/**
 * Checks whether the given value is already the flat backend error DTO shape:
 *   { type, code, message, param, data }
 * @param {*} value
 * @returns {boolean}
 */
function isFlatErrorBody(value) {
  return (
    !!value &&
    typeof value !== 'string' &&
    typeof value.code === 'string' &&
    typeof value.message === 'string'
  )
}

/**
 * Vuex actions in this codebase surface errors in one of two shapes:
 *  - The flat backend DTO directly: { type, code, message, param, data }
 *  - A VuexResponse-builder axios response wrapper: { status, data, headers, config }
 *    where `data` holds the flat backend DTO.
 *
 * This checks the flat shape first (since the backend DTO may itself contain
 * its own unrelated `data` field, e.g. InvalidCandidatesInTemplates' data:
 * { invalidCandidateIds }), and only falls back to unwrapping `.data` if that
 * check fails.
 *
 * @param {*} raw
 * @returns {*}
 */
function extractErrorBody(raw) {
  if (isFlatErrorBody(raw)) return raw
  return raw?.data ?? raw
}

/**
 * Normalises a backend error from a result-helper response object into an ErrorResponse model.
 *
 * Validates that the response contains a well-formed error object before
 * trusting it. If the error is absent or malformed, a safe generic fallback
 * is returned instead.
 *
 * Maps to the backend ErrorResponse structure:
 *   { type, code, message, param, data }
 *
 * @param {Object|null} response - A result-helper response object (from fail() / success())
 * @param {Object} $i18n - Vue i18n instance
 * @returns {ErrorResponse}
 */
export default function normaliseErrorFromResponse(response, $i18n) {
  const error = extractErrorBody(response?.error)
  const isValidError =
    error &&
    typeof error !== 'string' &&
    typeof error.code === 'string' &&
    typeof error.message === 'string'

  if (!isValidError) {
    return new ErrorResponse({
      _error: error,
      type: ErrorResponseType.unknown,
      code: CreateBookingErrorCodes.Unknown,
      message: $i18n.t('error.genericFailedRequestMessage'),
    })
  }

  return new ErrorResponse({
    _error: error,
    type: error.type ?? ErrorResponseType.unknown,
    code: error.code,
    // A blank/whitespace-only message is treated as missing so the alert
    // always has something to display, while still preserving the backend's
    // error code (and other fields) for any code-specific handling.
    message:
      error.message.trim() || $i18n.t('error.genericFailedRequestMessage'),
    param: error.param ?? null,
    data: error.data ?? null,
  })
}
